/**
 * Morse — die erklärenden Texte der Seite an EINER Stelle. (06.10.2026, Morse-Tabelle und leichterer Einstieg)
 *
 * Martin: „So ist es mir noch viel zu schwer.“ — und: „Und die Beschreibung, was die Übung kann, fehlt auch noch.“
 *
 * Regeln für jeden Satz hier:
 *   - einfache Wörter; kein Fachwort ohne Erklärung („Farnsworth“ heißt hier „gedehnte Pausen“, die
 *     „Koch-Methode“ wird erklärt, „WpM“ heißt „Wörter pro Minute“);
 *   - kurz: ein Absatz höchstens drei Zeilen am Telefon, keine Textwand;
 *   - die Seite liest NUR von hier (Test: jeder Reiter hat seine Beschreibung, und sie steht auf der Seite).
 */
import { KOCH_ANFAENGER_BIS_STUFE } from "../../shared/morse-koch.js";
import type { MorseReiter } from "./morse-teile.js";

/** Die Namen der Reiter, in ihrer Reihenfolge. */
export const REITER_NAME: Readonly<Record<MorseReiter, string>> = {
  hoeren: "Hören",
  geben: "Geben",
  tabelle: "Tabelle",
  fortschritt: "Fortschritt",
};

/** Ein bis zwei Sätze je Reiter, gleich unter der Reiter-Leiste: was man hier tut und was als Nächstes passiert. */
export const REITER_TEXT: Readonly<Record<MorseReiter, string>> = {
  hoeren: "Du hörst Gruppen aus fünf Zeichen und tippst, was du erkennst. Danach siehst du, was stimmte — Fehler klingen noch einmal langsam.",
  geben: "Du siehst ein Zeichen und gibst es mit der großen Taste: kurz drücken für Punkt, lang für Strich. Dann sagt dir die Seite, ob es stimmte.",
  tabelle: "Alle Zeichen zum Nachschlagen und Anhören.",
  fortschritt: "Hier siehst du, wie sicher du jedes Zeichen kannst und wann ein neues dazukommt.",
};

/** Die kurze Anleitung im Bereich „Bereit“ — was eine Runde ist (der Reiter-Text sagt schon, was man tut). */
export const ANLEITUNG = {
  hoerenGruppen: "Eine Runde: zehn Gruppen, nach jeder bleibt Zeit zum Tippen. Zwei bis drei Minuten.",
  hoerenWoerter: "Eine Runde: zehn Wörter, nach jedem bleibt Zeit zum Tippen.",
  geben: "Vor „Start“ kannst du die Taste frei ausprobieren: kurz halten = Punkt, lang halten = Strich.",
} as const;

/* ── Die Einführung („Wie funktioniert das?“) ─────────────────────── */

interface EinfuehrungsAbschnitt {
  titel: string;
  absaetze: string[];
  /** Eine Liste (die vier Reiter): Name und was er tut. */
  liste?: Array<{ name: string; text: string }>;
  /** Ein kleines Bild: Punkt und Strich in ihrem Verhältnis, dazu das A. */
  bild?: "punkt-strich";
}

export const EINFUEHRUNG = {
  knopf: "Wie funktioniert das?",
  titel: "So funktioniert die Übung",
  einleitung: "Hier lernst du Morse in ein paar Minuten am Tag — erst mit dem Ohr, dann mit der Hand.",
  schliessen: "Verstanden",
  abschnitte: [
    {
      titel: "Was Morse ist",
      absaetze: [
        "Jedes Zeichen ist eine Folge kurzer und langer Töne: Punkte und Striche. Ein Strich ist dreimal so lang wie ein Punkt.",
        "Gesprochen heißt der Punkt „di“ (am Ende „dit“), der Strich „dah“. Ein A ist di-dah.",
      ],
      bild: "punkt-strich",
    },
    {
      titel: "Wie du hier lernst",
      absaetze: [
        "Du beginnst mit zwei Zeichen, K und M. Ein neues kommt erst, wenn die bisherigen sicher sitzen: 9 von 10 richtig.",
        "Du lernst den Klang jedes Zeichens wie ein Wort, statt Punkte zu zählen. So lernt man nach der Koch-Methode.",
      ],
    },
    {
      titel: "Die vier Reiter",
      absaetze: ["„Start“ beginnt eine Runde. „Stopp“ beendet sie sofort und speichert, was du schon geschafft hast."],
      liste: [
        { name: REITER_NAME.hoeren, text: "du hörst Gruppen aus fünf Zeichen und tippst, was du erkennst." },
        { name: REITER_NAME.geben, text: "du morst selbst mit der großen Taste." },
        { name: REITER_NAME.tabelle, text: "alle Zeichen zum Nachschlagen und Anhören." },
        { name: REITER_NAME.fortschritt, text: "wie sicher du jedes Zeichen kannst." },
      ],
    },
    {
      titel: "Wie viel Zeit",
      absaetze: ["Lieber kurz und oft: 5 bis 10 Minuten am Tag reichen. Eine Hörrunde dauert zwei bis drei Minuten."],
    },
    {
      titel: "Kennenlernen und Hilfe",
      absaetze: [
        "Kennenlernen: Kommt ein neues Zeichen dazu, siehst und hörst du es zuerst in Ruhe — langsam und im normalen Tempo.",
        "Hilfe: Beim Hören stehen deine Zeichen unten, beim Geben die Silben. Auf den ersten Stufen ist sie von selbst an.",
      ],
    },
  ] as EinfuehrungsAbschnitt[],
  /** Die Beschriftung des kleinen Bilds im ersten Abschnitt. */
  bildPunkt: "Punkt: kurz",
  bildStrich: "Strich: dreimal so lang",
  bildBeispiel: "A",
} as const;

/* ── Kennenlernen ─────────────────────────────────────────────────── */

export const KENNENLERNEN = {
  titel: (zeichen: readonly string[]): string => (zeichen.length > 1 ? `Neu: ${zeichen.join(" und ")}` : `Neues Zeichen: ${zeichen[0] ?? ""}`),
  stufe: (stufe: number): string => `Kennenlernen · Stufe ${stufe}`,
  text: (zeichen: readonly string[]): string =>
    zeichen.length > 1
      ? "Hör dir beide zweimal an — erst langsam, dann im richtigen Tempo. Sprich die Silben leise mit. Dann oben auf Start."
      : "Hör es dir zweimal an — erst langsam, dann im richtigen Tempo. Sprich die Silben leise mit. Dann oben auf Start.",
  anhoeren: "Anhören",
  nochmal: "Nochmal hören",
} as const;

/* ── Merkwort (Eselsbrücke) ───────────────────────────────────────── */

/** Die Merkhilfe eines Zeichens (shared/morse-merkwoerter.ts): Silbe mit „o“ = Strich. Für Vorleser mit Wort davor. */
export const MERKWORT = {
  vorlesen: "Merkwort",
  aria: (text: string): string => `Merkwort ${text}`,
} as const;

/* ── Hilfe ────────────────────────────────────────────────────────── */

export const HILFE = {
  titel: "Hilfe",
  hoeren: {
    aus: "Ohne Hilfe — nur dein Ohr.",
    nach: "Nach dem Ton siehst du deine Zeichen mit ihrem Muster. Das Muster der Gruppe kommt erst nach deiner Antwort.",
    vorab: "Das Muster der Gruppe steht schon da, während sie klingt — ein Spickzettel zum Einstieg.",
    immer: "Deine Zeichen mit ihrem Muster stehen die ganze Runde unten, auch während es klingt. Das Muster der Gruppe kommt erst nach deiner Antwort.",
  },
  geben: {
    aus: "Nur die Vorlage mit ihrem Muster.",
    an: "Zur Vorlage stehen die Silben, und deine Zeichen bleiben unter der Taste sichtbar.",
  },
  /** Die Zeile über dem Muster beim Hören — nach dem Ton bzw. schon während er klingt. */
  musterTitel: { nach: "So klang es", vorab: "So klingt es", wartet: "Das Muster kommt nach deiner Antwort" },
  /** Die Wahl beim Hören und beim Geben. */
  hoerenWahl: { aus: "Aus", nach: "Nach dem Ton", vorab: "Vorher zeigen", immer: "Immer" },
  gebenWahl: { aus: "Aus", an: "An" },
  /** Die Zeile über den Zeichen der Stufe (Spickzettel). */
  spickTitel: "Deine Zeichen",
} as const;

/**
 * „Üben mit“ (08.10.2026, Martin: „Falls ich alle üben möchte, wäre es cool, wenn ich alle nehmen könnte und nicht nur das
 * neu hinzugefügte.“): die Wahl im Bereich „Bereit“ von Hören und Geben — EINE Wahl für beide, am Gerät gemerkt.
 * Der Satz zu „Alle Zeichen“ sagt klar, was es mit dem Aufstieg auf sich hat.
 */
export const UMFANG = {
  titel: "Üben mit",
  wahl: { meine: "Meine Zeichen", alle: "Alle Zeichen" },
  hinweis: {
    meine: (n: number): string => `Du übst die ${n} Zeichen deiner Stufe. Mit „9 von 10 richtig“ kommt das nächste dazu.`,
    alle: (n: number): string =>
      `Du übst alle ${n} Zeichen des Kurses, bunt gemischt. Jedes Zeichen merkt sich, wie gut es sitzt. Für den Aufstieg zählt das nicht — der geht nur mit „Meine Zeichen“.`,
  },
  /** Mitten in einer Runde (läuft oder ruht) lässt sich die Wahl nicht ändern. */
  sperre: "Du bist mitten in einer Runde. Beende sie zuerst mit „Stopp“ im Reiter Hören oder Geben, dann kannst du umschalten.",
  /** Die Zeile über den Zeichen im Bereich „Bereit“ (Hören). */
  zeichenTitel: "Alle Zeichen — antippen zum Anhören",
  zeichenGruppe: "Alle Zeichen des Kurses",
  /** Spickzettel „Alle Zeichen“: der Titel und die Erklärung der blassen Zeichen. */
  spickTitel: "Alle Zeichen",
  blass: "Blass: noch nie gehört.",
  nochNieGehoert: "noch nie gehört",
  /** Nach der Runde, wenn sie mit „Alle Zeichen“ lief. */
  fertig: "Mit allen Zeichen geübt: Jedes Zeichen hat seine Güte bekommen, für den Aufstieg zählt die Runde nicht.",
} as const;

/** Die Kurztabelle beim Geben: der Schalter „Tabelle“ nahe der Taste (frontend/lib/morse-geben.ts). */
export const KURZ = {
  knopf: "Tabelle",
  an: "ein",
  aus: "aus",
  /** Die Hilfe „An“ zeigt dieselben Zeichen schon — der Schalter steht dann fest auf „ein“. */
  durchHilfe: "ein · durch Hilfe",
  beschreibung: "Die Zeichen deiner Stufe mit ihrem Muster, unter der Taste",
} as const;

/* ── Die Tabelle ──────────────────────────────────────────────────── */

export const TABELLE = {
  /** Die schlanke Zeile über den Zeichen: „● kurz  ▬ lang · Antippen spielt das Zeichen“ (die Wörter vor „kurz“ und „lang“ nur zum Vorlesen). */
  punktWort: "Punkt",
  punkt: "kurz",
  strichWort: "Strich",
  strich: "lang",
  lesenNach: "Antippen spielt das Zeichen",
  ordnung: "Ordnung",
  ordnungen: { koch: "Meine Reihenfolge", alphabet: "Alphabet", muster: "Nach Muster" },
  status: {
    neu: "neu",
    // Kurz genug für das Feld am Telefon (drei Spalten, ≈ 90 px Text): „noch nicht gehört“ brach um und machte die Reihe höher.
    nichtGehoert: "noch ungehört",
    sicher: (p: string): string => `${p} sicher`,
    spaeter: (stufe: number): string => `ab Stufe ${stufe}`,
    extra: "nicht im Kurs",
  },
  gruppen: {
    deine: (stufe: number): string => `Deine Zeichen bis Stufe ${stufe}`,
    deineHinweis: "In dieser Reihenfolge kommen sie dazu (Koch-Methode).",
    spaeter: "Kommen noch",
    extra: "Nicht im Kurs",
    extraHinweis: "Kommen in den Übungen nicht vor — zum Nachschlagen.",
    buchstaben: "Buchstaben",
    umlaute: "Umlaute, ß und CH",
    ziffern: "Ziffern",
    satzzeichen: "Satzzeichen",
    laenge: (n: number): string => (n === 1 ? "Ein Ton" : `${n} Töne`),
    laengeHinweis: (n: number): string =>
      n === 1
        ? "Die kürzesten Zeichen: E und T."
        : n === 2
          ? "Je weiter unten, desto mehr Töne hat ein Zeichen. Vorne stehen die mit mehr Punkten, hinten die mit mehr Strichen."
          : "",
  },
  /** Nur wenn die Übung lief: ein Tipp führt zu ihr zurück und setzt sie fort. Sonst leistet der Reiter darüber dasselbe. */
  zurueckWeiter: "Zurück zur Übung",
} as const;

/* ── Die Hörspur: Vorführung und „Alle der Reihe nach“ ───────────── */

/**
 * Martin (07.10.2026): „… so funktioniert die Übung auch mit einem Abspielbutton, dass man sich anhören kann,
 * wie alles funktioniert. Also eine Audio-Spur zum Anhören.“ Nur Töne mit mitlaufender Textzeile (Untertitel) —
 * keine Sprachausgabe. Jeder Satz hier steht für ein Kapitel der Liste (morse-hoerspur-liste.ts); später kann
 * je Kapitel eine Stimme dazukommen, ohne dass sich die Kapitel ändern.
 */
export const HOERSPUR = {
  vorfuehrung: {
    start: "Vorführung anhören",
    /** Die Zeile unter dem Knopf: wie lange es dauert und was man tun muss (nichts). */
    hinweis: (minuten: number): string => `Etwa ${minuten === 1 ? "eine Minute" : `${minuten} Minuten`} zum Zuhören — du musst nichts tippen.`,
    titel: "Vorführung",
  },
  tabelle: {
    start: "Alle der Reihe nach",
    hinweis: "Spielt jedes Zeichen der Tabelle einmal vor, so wie sie gerade geordnet ist.",
    titel: (ordnung: string): string => `Alle der Reihe nach · ${ordnung}`,
    zeichen: (i: number, von: number): string => `Zeichen ${i} von ${von}`,
  },
  karte: {
    zustand: { spielt: "Spielt", pause: "Pause" },
    pause: "Pause",
    weiter: "Weiter",
    stopp: "Stopp",
    voriges: "Voriges",
    vorigesLang: "Voriges Kapitel",
    naechstes: "Nächstes",
    naechstesLang: "Nächstes Kapitel",
    fortschritt: "Fortschritt der Wiedergabe",
    /** Die Karte der Vorführung nennt sich selbst — sonst sähe sie wie die Karte „Kennenlernen“ darunter aus (Prüfbericht K7). */
    kapitel: (i: number, von: number, titel: string): string => `Vorführung · Kapitel ${i} von ${von}: ${titel}`,
    zeit: (jetzt: string, gesamt: string): string => `${jetzt} von ${gesamt}`,
  },
  zurueckZurUebung: "Zurück zur Übung",
  meldung: {
    fertig: "Das war die Vorführung. Wenn du magst, tipp jetzt auf „Start“ und probier es selbst.",
    fertigTabelle: "Das waren alle Zeichen der Tabelle.",
    /**
     * Die Sprechtaste nimmt in der Seite auf: dann spielt die Hörspur nicht. Es wartet nichts und startet nichts von
     * selbst — der Satz sagt, was zu tun ist (`knopf`: der Name des Knopfs).
     */
    sprechtaste: (knopf: string): string => `Die Sprechtaste nimmt gerade auf. Wenn du fertig bist, tipp noch einmal auf „${knopf}“.`,
    /** In der Karte, solange die Tagesaufnahme für die Hörspur angehalten ist (morse-spur-halt.ts). */
    tagesaufnahmeAngehalten: (liste: "demo" | "tabelle"): string =>
      `Die Tagesaufnahme ist ${liste === "tabelle" ? "fürs Vorspielen" : "für die Vorführung"} kurz angehalten und läuft danach weiter.`,
    /**
     * Im Browser kennt der Rekorder keine Pause (browser-recorder.ts), und auch der Knopf oben hält dort nichts an — der
     * Satz schickt nicht zu einem Knopf, der nichts tut: die App kann es, oder man beendet die Aufnahme.
     */
    tagesaufnahmeImBrowser: (liste: "demo" | "tabelle"): string =>
      `Im Browser lässt sich die Tagesaufnahme nicht kurz anhalten. Hör ${liste === "tabelle" ? "„Alle der Reihe nach“" : "die Vorführung"} in der App – oder beende die Aufnahme oben.`,
    /** Das Anhalten kam nicht rechtzeitig an oder scheiterte. Kommt es später doch, läuft die Aufnahme gleich danach weiter. */
    tagesaufnahmeNichtAngehalten: (knopf: string): string =>
      `Die Tagesaufnahme ließ sich gerade nicht kurz anhalten und läuft weiter. Tipp gleich noch einmal auf „${knopf}“.`,
    /** Die Tagesaufnahme nimmt (wieder) auf, während die Hörspur spielte — von außen fortgesetzt oder neu begonnen. */
    tagesaufnahmeLaeuftWieder: (liste: "demo" | "tabelle", knopf: string): string =>
      `Die Tagesaufnahme nimmt wieder auf, darum hat ${liste === "tabelle" ? "das Vorspielen" : "die Vorführung"} angehalten. Tipp noch einmal auf „${knopf}“.`,
    /** Die Notbremse des Halts: die Spur dauerte länger als vorgesehen. */
    zeitgrenze: "Das Abspielen hat länger gedauert als vorgesehen und ist darum beendet. Die Tagesaufnahme läuft wieder.",
  },
  /** Kapitel 1 — Punkt und Strich. */
  punktStrich: {
    titel: "Punkt und Strich",
    kurz: "Kurz: ein Punkt, so klingt „di“. Du hörst ihn dreimal.",
    lang: "Lang: ein Strich, so klingt „dah“ — dreimal so lang wie der Punkt. Auch ihn hörst du dreimal.",
  },
  /** Kapitel 2 — das neue Zeichen (auf Stufe 1: K und M). */
  neu: {
    titel: (anzahl: number): string => (anzahl > 1 ? "Deine neuen Zeichen" : "Dein neues Zeichen"),
    vorstellung: (stufe: number, zeichen: ReadonlyArray<{ zeichen: string; silben: string }>): string =>
      `Auf Stufe ${stufe} ${zeichen.length > 1 ? "sind" : "ist"} ${zeichen.map((z) => z.zeichen).join(" und ")} neu: ${zeichen.map((z) => `${z.zeichen} = ${z.silben}`).join(" und ")}.`,
    langsam: (z: string, silben: string): string => `${z} = ${silben} — erst langsam.`,
    normal: (z: string, silben: string): string => `${z} = ${silben} — jetzt im richtigen Tempo.`,
  },
  /** Kapitel 3 — eine Übungsaufgabe. */
  aufgabe: {
    titel: "Eine Übungsaufgabe",
    vorher: "So übst du beim Hören: Du hörst eine Gruppe aus fünf Zeichen.",
    hoere: "Hör genau hin …",
    tippen: (gruppe: string): string => `Du tippst, was du gehört hast: ${gruppe}`,
    danach: "Danach siehst du, was stimmte. Fehler klingen noch einmal langsam.",
  },
  /** Kapitel 4 — das Tempo: dasselbe Zeichen dreimal, mit dem einen Satz, was „Zeichen schnell, Pausen länger“ heißt. */
  tempo: {
    titel: "Das Tempo",
    satz: "Zeichen schnell, Pausen länger: Jedes Zeichen klingt im vollen Tempo, nur die Pausen dazwischen sind gedehnt. So hörst du den Klang als Ganzes und hast trotzdem Zeit zum Erkennen.",
    langsam: (t: number, w: number): string => `Langsam: ${t} / ${w} WpM — alles gedehnt.`,
    dein: (t: number, w: number): string => `Dein Tempo: ${t} / ${w} WpM — erst die Zeichen, dann die Pausen (Wörter pro Minute).`,
    ohneDehnung: (t: number): string => `Pausen nicht mehr gedehnt: ${t} / ${t} WpM — dieselben Zeichen, kürzere Pausen.`,
    schneller: (t: number): string => `Etwas schneller: ${t} / ${t} WpM.`,
  },
} as const;

/* ── Sprechen: die Antwort einsprechen statt tippen ─────────────── */

/**
 * Martin (08.10.2026): „Super wäre, wenn ich statt Buchstaben zu drücken Buchstaben aussprechen könnte, während des
 * Trainings, wenn die Pieptöne reinkommen, als zusätzliche Möglichkeit, Buchstaben einzugeben.“ — und: „Beides wäre
 * gut“ (am Stück UND Buchstabe für Buchstabe). Der Knopf „Sprechen“ steht im Hören-Reiter, sobald eine Gruppe zu
 * Ende klingt (das Mikrofon würde die Töne mithören). Was erkannt ist, steht im Feld wie Getipptes; abgeschickt wird erst
 * mit Enter. Jeder Fehler hat einen Satz — nie stilles Nichts.
 */
export const SPRECHEN = {
  knopf: "Sprechen",
  /** Der Knopf, solange die Gruppe noch klingt: grau, aber mit dem Grund (Martin 09.10.: „ausgegraut“, ohne zu wissen warum). */
  knopfWartet: "Sprechen (nach dem Ton)",
  /** Der Knopf, solange das Mikrofon zuhört: Antippen beendet die Aufnahme. */
  fertig: "Fertig",
  versteht: "Einen Moment …",
  aria: {
    bereit: "Antwort sprechen",
    nimmtAuf: "Aufnahme beenden und auswerten",
    versteht: "Ich werte aus",
    wartet: "Antwort sprechen, geht nach dem Ton",
    gesperrt: "Antwort sprechen, geht gerade nicht",
  },
  /** Im Bereich „Bereit“: dass es das gibt. */
  anleitung: "Du kannst deine Antwort auch sprechen: „K M U R E“ am Stück, oder ein Zeichen nach dem anderen — Buchstabe oder Merkwort („M“ oder „Monitor“). Mit „Berta“, „Dora“ … wird nichts verwechselt.",
  /** Während die Gruppe klingt. */
  nachDemTon: "Sprechen geht, sobald der Ton zu Ende ist.",
  hoert: "Ich höre zu … Tipp auf „Fertig“, wenn du fertig bist.",
  sperre: {
    anruf: "Im Anruf geht Sprechen nicht. Tippen geht weiter.",
    sprechtaste: "Die Sprechtaste nimmt gerade auf. Danach geht Sprechen wieder.",
    wiedergabe: "Gerade spielt etwas. Danach geht Sprechen wieder.",
  },
  fehler: {
    recht: "Das Mikrofon ist nicht freigegeben. Erlaub es in den Einstellungen deines Geräts (im Browser: das Schloss neben der Adresse). Tippen geht weiter.",
    keinMikro: "Auf diesem Gerät wurde kein Mikrofon gefunden. Tippen geht weiter.",
    netz: "Die Spracherkennung ist gerade nicht erreichbar (Netz oder Dienst). Tipp die Gruppe oder versuch es gleich noch einmal.",
    sonst: "Die Aufnahme hat nicht geklappt. Tipp die Gruppe oder versuch es noch einmal.",
    nichts: "Ich habe nichts gehört. Sprich nach dem Tipp deutlich, zum Beispiel „K M U R E“.",
    nichtsVerstanden: (gehoert: string): string =>
      `Aus „${gehoert}“ konnte ich keine Zeichen lesen. Sprich einzelne Zeichen, zum Beispiel „K M U R E“ oder „Kaufmann Martha“.`,
    voll: "Das Feld ist schon voll. Lösch ein Zeichen, wenn du etwas ändern willst.",
  },
  ergebnis: {
    gehoert: (text: string): string => `Gehört: „${text}“.`,
    pruefen: "Prüf die Zeichen, dann Enter.",
    luecken: (n: number): string =>
      n === 1
        ? "Ein Zeichen war unsicher und bleibt leer (·). Tipp unten das richtige oder schreib es selbst."
        : `${n} Zeichen waren unsicher und bleiben leer (·). Tipp unten die richtigen oder schreib sie selbst.`,
    zuviel: (n: number, laenge: number): string =>
      `Du hast ${n === 1 ? "ein Zeichen" : `${n} Zeichen`} mehr gesagt, als die Gruppe lang ist (${laenge}). Das Überzählige habe ich weggelassen.`,
    unverstanden: (liste: string): string => `Nicht verstanden: ${liste}.`,
  },
  wahl: {
    gruppe: "Unsichere Zeichen",
    stelle: (nr: number): string => `Zeichen ${nr}:`,
    aria: (nr: number, z: string): string => `Zeichen ${nr} ist ${z}`,
  },
} as const;

/* ── Tempo und Ton ────────────────────────────────────────────────── */

export const TEMPO = {
  erklaerung:
    "Das Zeichentempo bestimmt, wie schnell ein Zeichen selbst klingt; das wirksame Tempo dehnt nur die Pausen dazwischen, damit du Zeit zum Erkennen hast (WpM = Wörter pro Minute).",
  vorgabe: (stufe: number, tempo: number, wirksam: number): string =>
    `Vorgabe für Stufe ${stufe}: ${tempo} / ${wirksam} WpM${stufe <= KOCH_ANFAENGER_BIS_STUFE ? " — zum Einstieg langsamer" : ""}.`,
  vorgabeNehmen: "Vorgabe nehmen",
  /** K1: das eigene Tempo ist auf Stufe 1–2 schneller als der sanfte Start. */
  zuSchnell: (tempo: number, wirksam: number): string => `Dein Tempo ist schneller als der sanfte Start (${tempo}/${wirksam}). Zum Üben leichter: Vorgabe nehmen.`,
  schneller: (tempo: number, wirksam: number): string => ` Ab jetzt etwas schneller: ${tempo} / ${wirksam} WpM.`,
} as const;

/* ── Eine Stufe zurück / Stufe wählen ─────────────────────────────── */

const STUFE_WAHL = "Stufe wählen";

/** „R“, „R und E“, „R, E und S“ — bei langen Listen „28 Zeichen (von R bis 3)“, damit der Satz ein Satz bleibt. */
function zeichenListe(z: readonly string[]): string {
  if (z.length > 5) return `${z.length} Zeichen (von ${z[0]} bis ${z[z.length - 1]})`;
  return z.length > 1 ? `${z.slice(0, -1).join(", ")} und ${z[z.length - 1]}` : (z[0] ?? "");
}

/**
 * Martin (08.10.2026): „Gibt es die Möglichkeit, wenn man zu schnell voranschreitet, dass man wieder ein Level
 * zurückschalten kann? Mir ist jetzt ein Buchstabe zu viel.“ Der Satz VOR dem Umschalten sagt, was passiert; der
 * Satz danach bestätigt es und nennt den Weg zurück.
 */
export const STUFE = {
  titel: "Stufe ändern",
  erklaerung:
    "Zu viel auf einmal? Geh eine Stufe zurück: Das neueste Zeichen fällt dann vorerst heraus. Dein Stand bleibt gespeichert, und mit „9 von 10 richtig“ kommt es später wieder dazu.",
  /** Die schlichte Zeile im Reiter Hören, nach einem Aufstieg. */
  zeile: "Zu viel auf einmal?",
  zurueck: "Eine Stufe zurück",
  wahlTitel: STUFE_WAHL,
  wahlHinweis: (hoechste: number): string => `Bis Stufe ${hoechste} — so weit bist du schon gekommen.`,
  option: (stufe: number, neu: readonly string[], jetzt: boolean): string => `Stufe ${stufe} · neu: ${neu.join(" ")}${jetzt ? " (jetzt)" : ""}`,
  /** Was passiert, wenn man bestätigt. `weg`: fallen heraus; `dazu`: kommen (wieder) dazu. */
  frage: (von: number, bis: number, weg: readonly string[], dazu: readonly string[]): string =>
    weg.length
      ? `Stufe ${von} → ${bis}: ${zeichenListe(weg)} ${weg.length > 1 ? "fallen" : "fällt"} vorerst heraus, dein Stand bleibt gespeichert.`
      : `Stufe ${von} → ${bis}: ${zeichenListe(dazu)} ${dazu.length > 1 ? "sind" : "ist"} wieder dabei.`,
  ja: (bis: number): string => `Ja, Stufe ${bis} nehmen`,
  abbrechen: "Abbrechen",
  /** Nach dem Umschalten. `rueckweg`: wo man es rückgängig macht. */
  fertig: (von: number, bis: number, weg: readonly string[], dazu: readonly string[], rueckweg: boolean): string =>
    weg.length
      ? `Jetzt Stufe ${bis}. ${zeichenListe(weg)} ${weg.length > 1 ? "fallen" : "fällt"} vorerst heraus, dein Stand bleibt gespeichert. ${
          rueckweg ? `Rückgängig: oben bei „${STUFE_WAHL}“ Stufe ${von} nehmen.` : `Rückgängig geht im Reiter „Fortschritt“ bei „${STUFE_WAHL}“.`
        }`
      : `Jetzt Stufe ${bis}. ${zeichenListe(dazu)} ${dazu.length > 1 ? "sind" : "ist"} wieder dabei.`,
  /** Mitten in einer Runde (läuft oder pausiert): nicht umschalten. */
  sperre: "Du bist mitten in einer Runde. Beende sie zuerst mit „Stopp“ im Reiter Hören oder Geben, dann kannst du die Stufe ändern.",
  fehler: (grund: string): string => `Die Stufe ist nicht geändert: ${grund}`,
} as const;

/* ── Was als Nächstes dran ist (Fortschritt) ──────────────────────── */

/*
 * EIN Satz, was als Nächstes dran ist — 16.10.2026: nur noch EINE Fassung, in shared/morse-naechstes.ts (dort liest
 * auch Vespera, Werkzeug `morse_stand`). Vorher stand hier eine Wort-für-Wort-Kopie; der Wächter
 * tests/frontend/morse-stand-satz.test.ts hielt beide gleich. Mit dem Ziel der Stufe (`ziel`) sagt der Satz, welche
 * Bedingung des Aufstiegs fehlt.
 */
export { alsNaechstes } from "../../shared/morse-naechstes.js";

/* ── Augen zu (13.10.2026, morse-augen-zu.ts) ─────────────────────── */

/**
 * Martin hört einen Buchstaben und SAGT ihn — ohne hinzusehen, ohne Antippen. Die Sätze sind kurz: Wer die Augen zu hat, hört
 * sie nicht; wer hinsieht, liest sie in einem Blick.
 */
export const AUGEN = {
  modus: {
    titel: "Wie willst du üben?",
    tippen: "Tippen",
    augen: "Augen zu",
    sperre: "Mitten in einer Runde bleibt der Modus, wie er ist.",
  },
  erklaerung:
    "Du hörst einen Buchstaben und sagst ihn laut oder ein Merkwort dazu („M“ oder „Monitor“) — ohne hinzusehen und ohne etwas anzutippen. Richtig: ein heller Ton. Falsch: ein tiefer Ton, dann sagt eine Stimme den Buchstaben, er klingt langsam, und derselbe Buchstabe kommt gleich noch einmal (dieser zweite Versuch zählt nicht). Ein kurzer, leiser Tick sagt dir: Jetzt kommt ein neuer Buchstabe. Kommt nichts oder verstehe ich dich nicht, hörst du ihn noch einmal; danach sage ich dir, welcher es war — das zählt nicht als Fehler. Tippen auf „Stopp“ beendet die Runde.",
  menge: {
    titel: "Gerade dran",
    neu: "neu",
    aria: (zeichen: readonly string[]): string => `Gerade dran: ${zeichen.join(", ")}`,
  },
  /** „Buchstabe: Aus | Nach der Antwort | Immer“ — ob und wann der Buchstabe der Aufgabe auf dem Bildschirm steht. */
  anzeige: {
    titel: "Buchstabe zeigen",
    aus: "Aus",
    nach: "Nach der Antwort",
    immer: "Immer (zählt nicht)",
    erklaerung: {
      aus: "Der Buchstabe steht nie auf dem Bildschirm.",
      nach: "Der Buchstabe erscheint, sobald du geantwortet hast. „Zeigen“ blendet ihn früher ein — dann zählt die Aufgabe nicht.",
      immer: "Der Buchstabe steht immer da, auch während der Frage. Das ist Üben ohne Wertung: die Aufgaben zählen nicht für den Aufstieg.",
    },
    platzhalter: "?",
    zeigen: "Zeigen",
    zeigenAria: "Buchstaben jetzt einblenden — diese Aufgabe zählt dann nicht",
    aria: (z: string): string => `Buchstabe ${z}`,
  },
  /** Nicht sicher verstanden: die wahrscheinlichsten Buchstaben zum Antippen (21.10.2026, „zur Not drauf drücken“). */
  kandidaten: {
    titel: "Tipp an, was du gesagt hast — oder sag es noch einmal.",
    /** Nach dem letzten unsicheren Versuch: das Mikrofon ist zu, nur noch Tippen. */
    titelLetzt: "Tipp an, was du gesagt hast.",
    /** Im aria-live-Satz der Phase: die Felder in Worten (nur Gehörtes, nie die Lösung). */
    frage: (zeichen: readonly string[]): string => `Meintest du: ${zeichen.join(", ")}?`,
    gruppe: "Was hast du gesagt?",
    aria: (z: string): string => `${z} antippen — zählt wie gesagt`,
  },
  beginn: "Start",
  zaehler: (n: number, richtig: number): string => `Aufgabe ${n} · ${richtig} richtig`,
  phase: {
    vorlauf: "Gleich geht's los …",
    hoert: "Hör zu …",
    spricht: "Sag den Buchstaben …",
    wertet: "Einen Moment …",
    richtig: "Richtig!",
    /** Richtig, und er sagte ein Merkwort: „M — Monitor ✓“. */
    richtigMerkwort: (z: string, wort: string): string => `${z} — ${wort.charAt(0).toLocaleUpperCase("de")}${wort.slice(1)} ✓`,
    falsch: (z: string): string => `Das war ${z}.`,
    /** Nichts Zuordenbares gehört (auch nach dem Wiederholen) — ehrlich, nicht „falsch“; zählt nicht. */
    unklar: (z: string): string => `Nicht verstanden. Das war ${z}.`,
    nochmal: "Noch einmal …",
    /** Der letzte Versuch war unsicher: die Felder warten noch kurz. */
    tippAn: "Nicht sicher verstanden.",
    /** Nach einem Fehler: DERSELBE Buchstabe wird gleich noch einmal gestellt (zählt nicht, nur der erste Versuch). */
    nochmalGleich: "Nochmal derselbe Buchstabe …",
    /** Vor jedem NEUEN Buchstaben, nach einem kurzen Tick. */
    neu: "Neuer Buchstabe …",
    pause: "Pause.",
  },
  /** Was die Stimme sagt, wenn er falsch lag (≤ 300 Zeichen, ein kurzer Satz). */
  stimme: (z: string): string => `Das war ${z}.`,
  /** Was die Stimme sagt, wenn nichts zu verstehen war — kein tiefer Ton davor, es war kein Fehler. */
  stimmeUnklar: (z: string): string => `Nicht verstanden. Das war ${z}.`,
  pause: {
    knopf: "Pause. „Weiter“ spielt den Buchstaben noch einmal.",
    naechste: "Pause. „Weiter“ spielt den nächsten Buchstaben.",
    stille: "Pause, weil acht Aufgaben in Folge keine Antwort hatten. „Weiter“ spielt den nächsten Buchstaben.",
    sperre: (grund: string): string => `Pause. ${grund} „Weiter“ versucht es noch einmal.`,
  },
  fehler: {
    recht: "Das Mikrofon ist nicht freigegeben. Erlaub es in den Einstellungen deines Geräts (im Browser: das Schloss neben der Adresse). Dann „Weiter“.",
    keinMikro: "Auf diesem Gerät wurde kein Mikrofon gefunden.",
    netz: "Die Spracherkennung ist gerade nicht erreichbar (Netz oder Dienst). Danach „Weiter“.",
    sonst: "Das Mikrofon hat nicht geklappt. Tipp auf „Weiter“, um es noch einmal zu versuchen.",
  },
  fertig: {
    leer: "Keine Aufgabe beantwortet — nichts gespeichert.",
    ergebnis: (richtig: number, gesamt: number, aufgaben: number): string =>
      `${richtig} von ${gesamt} richtig · ${aufgaben} ${aufgaben === 1 ? "Aufgabe" : "Aufgaben"}`,
  },
} as const;
